import { Phone, MapPin, Plus } from 'lucide-react';
import { ProjectContact } from '@/types';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface ProjectQuickActionsProps {
  address: string;
  contacts: ProjectContact[];
  onNewDoc: () => void;
}

export default function ProjectQuickActions({ address, contacts, onNewDoc }: ProjectQuickActionsProps) {
  const getProjectWazeUrl = () => `https://waze.com/ul?q=${encodeURIComponent(address)}`;
  const getProjectWazeMessage = () => `הנה מיקום הפרויקט:\n${address}\n${getProjectWazeUrl()}`;
  const normalizeWhatsAppPhone = (phone: string) => {
    let normalized = phone.replace(/\D/g, '');
    if (normalized.startsWith('0')) {
      normalized = `972${normalized.slice(1)}`;
    }
    return normalized;
  };

  return (
    <div className="flex gap-2 mb-4">
      {contacts.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>אנשי קשר</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" sideOffset={8} className="w-56 rounded-xl border border-border bg-card shadow-soft">
            {contacts.map((contact, index) => (
              <DropdownMenuItem
                className="px-4 py-3 w-4"
                key={contact.id ?? `${contact.phone}-${index}`}
                onSelect={() => {
                  window.location.href = `tel:${contact.phone}`;
                }}
              >
                <div className="flex flex-col text-right">
                  <span>{contact.name}</span>
                  <span className="text-xs text-muted-foreground">{contact.phone}</span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <button
          type="button"
          disabled
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-secondary text-secondary-foreground/70 font-medium cursor-not-allowed"
        >
          <Phone className="w-4 h-4" />
          <span>אנשי קשר</span>
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80 transition-colors"
          >
            <MapPin className="w-4 h-4" />
            <span>ניווט</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={8} className="w-56 rounded-xl border border-border bg-card shadow-soft">
          <DropdownMenuItem
            className="px-4 py-3"
            onSelect={() => {
              const whatsappUrl = `https://waze.com/ul?q=${encodeURIComponent(address)}`;
              window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            <div className="flex flex-col text-left">
              <span>ניווט ב-waze</span>
              <span className="text-xs text-muted-foreground">נווט ישירות לאתר</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="px-4 py-3"
            onSelect={() => {
              const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(getProjectWazeMessage())}`;
              window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            <div className="flex flex-col text-left">
              <span>שלח לוואטסאפ</span>
              <span className="text-xs text-muted-foreground">פתח רשימת אנשי קשר בוואטסאפ</span>
            </div>
          </DropdownMenuItem>
          {contacts.length > 0 && <DropdownMenuSeparator />}
          {contacts.map((contact, index) => {
            const whatsappPhone = normalizeWhatsAppPhone(contact.phone);
            const whatsappUrl = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(getProjectWazeMessage())}`;
            return (
              <DropdownMenuItem
                className="px-4 py-3"
                key={contact.id ?? `${contact.phone}-${index}`}
                onSelect={() => {
                  window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                <div className="flex flex-col text-right">
                  <span>{contact.name}</span>
                  <span className="text-xs text-muted-foreground">{contact.phone}</span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        onClick={onNewDoc}
        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl gradient-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
      >
        <Plus className="w-4 h-4" />
        <span>תיעוד</span>
      </button>
    </div>
  );
}
